// V-23 家人行事曆（網頁；docs/panel/09-voice-calendar.md §5.3、§9.2、§9.7；05-ui-ux V-23；VC1a）。
// 路由 /devices/:id/calendar。只限行事曆家人（主人 + 能管行事曆的家人）；其他人 403 CALENDAR_FORBIDDEN → 顯示一句說明。
//   - 月 / 週 / 日檢視 + ［今天］＋ 上 / 下一頁；時間一律標「面板當地時間」，看的人在別的時區另外顯示換算後的時間。
//   - 新增 / 編輯 / 刪除；重複事件存檔 / 刪除前選「只改這一次 / 這次以後 / 全部」並確認（整串帶 chain_rev、單次帶 rev）。
//   - 409 CALENDAR_CONFLICT →「這件事剛被〇〇改過」（details.latest.updated_by_name；沒有就「別人」）［看最新的］重新整理。
//   - 分割過的一串（同 chain_id）在畫面上是同一件事；詳細頁的規則取最新那段。
//   - VC1b：要確認的那一次在時間窗內（準時前 10 分鐘 ~ 後 6 小時）、還沒確認 → ［阿嬤說吃過了］代確認（calendar:ack）；
//     頁面上方列出「還沒確認」的；詳細頁有確認紀錄（同一件事各次的確認照時間排：幾點按的、語音確認、誰代確認、待核准網路）。
//   - 從通知點進來：?event=&rid= → 跳到那天、打開那一次；?edit=1&new_start=&new_title=（ask_family）→ 直接開編輯並預填
//     （上方顯示「原本：…」）；?del=1（ask_family 想取消）→ 直接開刪除確認（重複的先問哪幾次）。
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { deviceName } from './deviceModel';
import { useDeviceList } from './useDeviceList';
import {
  ALL_DAY_REMINDER_OPTIONS, CAL_CATEGORIES, CAL_MAX_REMINDERS, TIMED_REMINDER_OPTIONS, WEEKDAY_CODES,
  allowedScopes, calErr, calErrKey, createBody, createCalEvent, deleteCalEvent, draftError, draftFromOccurrence, getPanelCalendar,
  groupByDay, ifMatchFor, isRecurringOcc, latestOfChain, monthGrid, newDraft, nowWall, parseRRule, patchCalEvent, patchChanges,
  reminderId, reminderLabel, repeatLabel, scopeNoteKey, conflictByName, forWhomOptions, shiftAnchor, viewRange, viewerTz, viewerWall, wallDate, wallTime, weekDays, weekdayOf,
  ackCalOccurrence, ackHistory, ackLabel, ackRecordParts, canFamilyAck, dueWall, findOccurrence, prefillDraft, wallMinutes,
  type CalDraft, type CalOccurrence, type CalReminder, type CalScope, type CalViewMode, type CalendarView, type RepeatKind, type RepeatSpec,
} from './panelCalendar';
import './panelCalendar.css';

const CAT_ICON: Record<string, string> = { med: '💊', doctor: '🏥', birthday: '🎂', other: '•' };

type Editing = { kind: 'new'; draft: CalDraft } | { kind: 'edit'; occ: CalOccurrence; before: CalDraft; draft: CalDraft; fromNotice?: boolean };
type ScopeAsk = {
  mode: 'edit' | 'delete'; occ: CalOccurrence; scopes: CalScope[]; scope: CalScope; noteKey: string | null; clearWarn: boolean;
  /** 修改時點的那一次的新開始時間（算確認框小字用） */
  newStart?: string;
  run: (s: CalScope) => Promise<void>;
};

export function PanelCalendarPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [search, setSearch] = useSearchParams();
  /** 從通知點進來要打開的那一次（讀到資料後用一次就清掉） */
  const openReq = useRef<{ event: string; rid?: string; edit: boolean; del: boolean; newStart?: string; newTitle?: string } | null>(
    search.get('event') ? {
      event: search.get('event') ?? '', rid: search.get('rid') ?? undefined, edit: search.get('edit') === '1', del: search.get('del') === '1',
      newStart: search.get('new_start') ?? undefined, newTitle: search.get('new_title') ?? undefined,
    } : null,
  );
  const { devices } = useDeviceList();
  const device = devices?.find((d) => d.device_id === id);
  const name = device ? deviceName(device) : '';

  const [mode, setMode] = useState<CalViewMode>('month');
  const [anchor, setAnchor] = useState<string | null>(null);
  const [view, setView] = useState<CalendarView | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [detail, setDetail] = useState<CalOccurrence | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [scopeAsk, setScopeAsk] = useState<ScopeAsk | null>(null);
  const [confirmDel, setConfirmDel] = useState<CalOccurrence | null>(null);
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  /** null = 沒衝突；字串 = 最後改的人（空 = 不知道是誰） */
  const [conflict, setConflict] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const handleLoadErr = useCallback((e: unknown) => {
    if (calErr(e).kind === 'forbidden') setForbidden(true);
    else setLoadErr(calErrKey(e));
  }, []);

  // 第一次：不帶範圍讀一次，拿面板的 tz 與「今天」（面板當地）
  useEffect(() => {
    let alive = true;
    getPanelCalendar(id)
      .then((v) => {
        if (!alive) return;
        const req = openReq.current;
        if (req?.rid && /^\d{4}-\d{2}-\d{2}/.test(req.rid)) {
          setAnchor(wallDate(req.rid));
          setMode('day');
        } else {
          setAnchor(wallDate(nowWall(v.now)) || v.from);
        }
      })
      .catch((e) => alive && handleLoadErr(e));
    return () => {
      alive = false;
    };
  }, [id, handleLoadErr]);

  const load = useCallback(async () => {
    if (!anchor) return;
    const r = viewRange(mode, anchor);
    try {
      const v = await getPanelCalendar(id, r.from, r.to);
      setView(v);
      setLoadErr(null);
    } catch (e) {
      handleLoadErr(e);
    }
  }, [id, mode, anchor, handleLoadErr]);

  useEffect(() => {
    void load();
  }, [load]);

  // 通知點進來：資料到了就打開那一次（找不到 = 已經被刪 / 不在範圍 → 顯示一句）
  useEffect(() => {
    const req = openReq.current;
    if (!req || !view) return;
    openReq.current = null;
    setSearch({}, { replace: true });
    const o = findOccurrence(view.occurrences, req.event, req.rid);
    if (!o) {
      setMsg(t('panelCal.notFoundFromNotice'));
      return;
    }
    if (req.del) {
      askDelete(o);
    } else if (req.edit) {
      const d = draftFromOccurrence(o, latestOfChain(view.occurrences, o));
      setEditing({ kind: 'edit', occ: o, before: d, draft: prefillDraft(d, req.newStart, req.newTitle), fromNotice: !!(req.newStart || req.newTitle) });
    } else {
      setDetail(o);
    }
    // askDelete 每次 render 都是新的；只在資料到了時用一次（openReq 用完清掉）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, setSearch, t]);

  const now = view ? nowWall(view.now) : '';
  const today = wallDate(now);
  const tz = view?.tz ?? '';
  const myTz = viewerTz();
  const byDay = useMemo(() => groupByDay(view?.occurrences ?? []), [view]);
  const residents = view?.residents ?? []; // 家人端 GET calendar 帶的 settings.residents（09 v0.8.3）
  // 已經到準時、還沒確認、還能代確認的（§5.3 代確認）
  const waitingAck = useMemo(() => {
    if (!view) return [];
    const nowM = wallMinutes(nowWall(view.now));
    return view.occurrences.filter((o) => canFamilyAck(o, view.now) && wallMinutes(dueWall(o)) <= nowM);
  }, [view]);

  const timeText = (o: CalOccurrence) => (o.all_day ? t('panelCal.allDay') : wallTime(o.start));
  const viewerText = (wall: string) => {
    const v = viewerWall(wall, tz, myTz);
    return v ? t('panelCal.viewerTime', { time: `${v.slice(5, 10).replace('-', '/')} ${v.slice(11, 16)}` }) : '';
  };
  const dayLabel = (d: string) => t('panelCal.dayLabel', { m: Number(d.slice(5, 7)), d: Number(d.slice(8, 10)), wd: t(`panelCal.wd.${WEEKDAY_CODES[weekdayOf(d)]}`) });

  const reloadAfterWrite = async (message: string) => {
    setEditing(null);
    setScopeAsk(null);
    setConfirmDel(null);
    setDetail(null);
    setMsg(message);
    await load();
  };

  const writeFailed = (e: unknown) => {
    if (calErr(e).kind === 'conflict') {
      // 別人先改了：關掉對話框，顯示「這件事剛被別人改過」［看最新的］
      setConflict(conflictByName(e));
      setScopeAsk(null);
      setEditing(null);
      setConfirmDel(null);
      setDetail(null);
      return;
    }
    setFormErr(calErrKey(e));
  };

  const run = async (fn: () => Promise<void>, okMsg: string) => {
    setBusy(true);
    setFormErr(null);
    try {
      await fn();
      await reloadAfterWrite(okMsg);
    } catch (e) {
      writeFailed(e);
    } finally {
      setBusy(false);
    }
  };

  // ── 存檔 ──
  const save = () => {
    if (!editing) return;
    const err = draftError(editing.draft);
    if (err) {
      setFormErr(err);
      return;
    }
    if (editing.kind === 'new') {
      void run(() => createCalEvent(id, createBody(editing.draft)), t('panelCal.added'));
      return;
    }
    const { occ, before, draft } = editing;
    const ch = patchChanges(before, draft);
    if (Object.keys(ch.body).length === 0) {
      setFormErr('panelCal.noChanges');
      return;
    }
    const scopes = allowedScopes(occ, ch);
    if (scopes === null) {
      setFormErr(ch.rruleCleared ? 'panelCal.err.clearRule' : 'panelCal.err.mixed');
      return;
    }
    const patch = (scope?: CalScope) => {
      const body = { ...ch.body };
      if (scope) {
        body.scope = scope;
        body.recurrence_id = occ.recurrence_id;
        if (scope === 'this') delete body.rrule;
        else delete body.all_day;
      }
      return patchCalEvent(id, occ.event_id, ifMatchFor(occ, scope), body);
    };
    if (scopes.length === 0) {
      void run(() => patch(), t('panelCal.saved'));
      return;
    }
    const first = scopes.includes('all') ? 'all' : scopes[0];
    setScopeAsk({
      mode: 'edit', occ, scopes, scope: first, noteKey: scopeNoteKey(occ, first, now, ch.body.start), clearWarn: ch.rruleChanged, newStart: ch.body.start,
      run: (s) => run(() => patch(s), t('panelCal.saved')),
    });
  };

  // ── 代確認（§5.3、§6.3）──
  const familyAck = (o: CalOccurrence) => {
    const l = ackLabel(o);
    void run(() => ackCalOccurrence(id, o).then(() => undefined), t('panelCal.ack.sent', { label: t(l.key, l.params) }));
  };

  // ── 刪除 ──
  const askDelete = (o: CalOccurrence) => {
    setFormErr(null);
    if (!isRecurringOcc(o)) {
      setConfirmDel(o);
      return;
    }
    setScopeAsk({
      mode: 'delete', occ: o, scopes: ['this', 'following', 'all'], scope: 'this', noteKey: null, clearWarn: false,
      run: (s) => run(() => deleteCalEvent(id, o.event_id, ifMatchFor(o, s), s, o.recurrence_id), t('panelCal.deleted')),
    });
  };

  const openEdit = (o: CalOccurrence) => {
    const occs = view?.occurrences ?? [];
    const d = draftFromOccurrence(o, latestOfChain(occs, o));
    setFormErr(null);
    setDetail(null);
    setEditing({ kind: 'edit', occ: o, before: d, draft: d });
  };

  const openNew = (date: string) => {
    setFormErr(null);
    setEditing({ kind: 'new', draft: newDraft(date) });
  };

  const reloadLatest = async () => {
    setConflict(null);
    setEditing(null);
    setDetail(null);
    setScopeAsk(null);
    setConfirmDel(null);
    await load();
  };

  if (forbidden) {
    return (
      <section className="pc-page" data-testid="panel-calendar">
        <header className="dv-detail-head">
          <h1>{t('panelCal.title')}</h1>
          <Link className="btn-ghost" to={`/devices/${encodeURIComponent(id)}`}>{t('panelCal.back')}</Link>
        </header>
        <div className="card" role="alert" data-testid="cal-forbidden">{t('panelCal.err_forbidden')}</div>
      </section>
    );
  }

  const prevKey = mode === 'month' ? 'prevMonth' : mode === 'week' ? 'prevWeek' : 'prevDay';
  const nextKey = mode === 'month' ? 'nextMonth' : mode === 'week' ? 'nextWeek' : 'nextDay';
  const periodLabel = !anchor ? '' : mode === 'month'
    ? t('panelCal.monthLabel', { y: anchor.slice(0, 4), m: Number(anchor.slice(5, 7)) })
    : mode === 'week' ? `${dayLabel(weekDays(anchor)[0])} – ${dayLabel(weekDays(anchor)[6])}` : dayLabel(anchor);

  return (
    <section className="pc-page" data-testid="panel-calendar">
      <header className="dv-detail-head">
        <div>
          <h1>{name ? t('panelCal.pageTitle', { name }) : t('panelCal.title')}</h1>
          {tz && (
            <p className="text-secondary pc-tz" data-testid="cal-tz">
              {t('panelCal.tzNote', { tz })}
              {viewerWall(`${today || '2026-01-01'}T12:00`, tz, myTz) && <> · {t('panelCal.tzNoteViewer', { tz: myTz })}</>}
            </p>
          )}
        </div>
        <Link className="btn-ghost" to={`/devices/${encodeURIComponent(id)}`}>{t('panelCal.back')}</Link>
      </header>

      <div className="pc-toolbar" role="toolbar">
        <div className="pc-seg" role="group">
          {(['month', 'week', 'day'] as CalViewMode[]).map((m) => (
            <button key={m} className={mode === m ? 'btn-primary' : 'btn-ghost'} aria-pressed={mode === m} onClick={() => setMode(m)} data-testid={`cal-view-${m}`}>
              {t(`panelCal.view.${m}`)}
            </button>
          ))}
        </div>
        <button className="btn-ghost" onClick={() => today && setAnchor(today)} data-testid="cal-today">{t('panelCal.today')}</button>
        <button className="btn-ghost" aria-label={t(`panelCal.${prevKey}`)} onClick={() => anchor && setAnchor(shiftAnchor(mode, anchor, -1))} data-testid="cal-prev">‹</button>
        <strong className="pc-period" data-testid="cal-period">{periodLabel}</strong>
        <button className="btn-ghost" aria-label={t(`panelCal.${nextKey}`)} onClick={() => anchor && setAnchor(shiftAnchor(mode, anchor, 1))} data-testid="cal-next">›</button>
        <span className="pc-spacer" />
        <button className="btn-primary" disabled={!anchor} onClick={() => anchor && openNew(mode !== 'day' && today && anchor.slice(0, 7) === today.slice(0, 7) ? today : anchor)} data-testid="cal-add">
          + {t('panelCal.add')}
        </button>
      </div>

      {msg && <p className="text-secondary" role="status" data-testid="cal-msg">{msg}</p>}
      {conflict !== null && (
        <div className="card pc-conflict" role="alert" data-testid="cal-conflict">
          <span>{conflict ? t('panelCal.conflictBy', { name: conflict }) : t('panelCal.conflict')}</span>
          <button className="btn-primary" onClick={() => void reloadLatest()}>{t('panelCal.conflictReload')}</button>
        </div>
      )}
      {loadErr && <p className="error-text" role="alert">{t(loadErr)}</p>}
      {waitingAck.length > 0 && (
        <div className="card pc-waiting" data-testid="cal-waiting-ack">
          <h2>{t('panelCal.ack.waitingTitle')}</h2>
          {formErr && !detail && !editing && !scopeAsk && !confirmDel && <p className="error-text" role="alert">{t(formErr)}</p>}
          <ul className="pc-waiting-list">
            {waitingAck.map((o) => {
              const l = ackLabel(o);
              return (
                <li key={`${o.event_id}-${o.recurrence_id}`} className="pc-waiting-row" data-testid="cal-waiting-item">
                  <span>
                    {CAT_ICON[o.category] ?? '•'} {o.all_day ? t('panelCal.allDay') : wallTime(o.start)} {o.title}
                    {o.for_whom && <span className="dv-badge"> {o.for_whom}</span>}
                  </span>
                  <span className="pc-waiting-actions">
                    <button className="btn-primary" disabled={busy} onClick={() => familyAck(o)} data-testid="cal-waiting-ack-btn">{t(l.key, l.params)}</button>
                    <button className="btn-ghost" onClick={() => setDetail(o)}>{t('panelCal.ack.details')}</button>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {!view && !loadErr && <p className="text-secondary">{t('panelCal.loading')}</p>}

      {view && anchor && mode === 'month' && (
        <div className="pc-month" data-testid="cal-month">
          {WEEKDAY_CODES.map((c) => <div key={c} className="pc-wdhead">{t(`panelCal.wd.${c}`)}</div>)}
          {monthGrid(anchor).map((d) => {
            const items = byDay[d] ?? [];
            const out = d.slice(0, 7) !== anchor.slice(0, 7);
            return (
              <button key={d} className={`pc-cell${out ? ' pc-out' : ''}${d === today ? ' pc-today' : ''}`} data-testid={`cal-cell-${d}`}
                onClick={() => { setAnchor(d); setMode('day'); }} aria-label={dayLabel(d)}>
                <span className="pc-num">{Number(d.slice(8, 10))}</span>
                {items.slice(0, 2).map((o) => (
                  <span key={`${o.event_id}-${o.recurrence_id}`} className="pc-chip">{CAT_ICON[o.category] ?? '•'} {o.title}</span>
                ))}
                {items.length > 2 && <span className="pc-more">{t('panelCal.more', { n: items.length - 2 })}</span>}
              </button>
            );
          })}
        </div>
      )}

      {view && anchor && mode === 'week' && (
        <div className="pc-week" data-testid="cal-week">
          {weekDays(anchor).map((d) => (
            <div key={d} className={`pc-wcol${d === today ? ' pc-today' : ''}`}>
              <button className="pc-wday btn-ghost" onClick={() => { setAnchor(d); setMode('day'); }}>{dayLabel(d)}</button>
              {(byDay[d] ?? []).map((o) => (
                <button key={`${o.event_id}-${o.recurrence_id}`} className={`pc-item${o.end < now && !o.all_day ? ' pc-past' : ''}`} onClick={() => setDetail(o)}>
                  <span className="pc-time">{timeText(o)}</span> {CAT_ICON[o.category] ?? '•'} {o.title}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      {view && anchor && mode === 'day' && (
        <div className="card pc-day" data-testid="cal-day">
          {(byDay[anchor] ?? []).length === 0 && <p className="text-secondary">{t('panelCal.emptyDay')}</p>}
          {(byDay[anchor] ?? []).map((o) => (
            <button key={`${o.event_id}-${o.recurrence_id}`} className={`pc-dayrow${(o.all_day ? wallDate(o.end) < today : o.end < now) ? ' pc-past' : ''}`} onClick={() => setDetail(o)}
              data-testid="cal-day-item">
              <span className="pc-time">{timeText(o)}{!o.all_day && <small className="text-secondary"> {viewerText(o.start)}</small>}</span>
              <span className="pc-daytitle">{CAT_ICON[o.category] ?? '•'} {o.title}</span>
              {o.for_whom && <span className="dv-badge">{o.for_whom}</span>}
              {o.location && <span className="text-secondary">{o.location}</span>}
            </button>
          ))}
        </div>
      )}

      {detail && (
        <Modal label={detail.title} onClose={() => setDetail(null)}>
          <CalDetail occ={detail} ruleSource={latestOfChain(view?.occurrences ?? [], detail)} history={ackHistory(view?.occurrences ?? [], detail)}
            viewerText={viewerText} dayLabel={dayLabel} />
          <div className="dv-pairing-actions">
            {view && canFamilyAck(detail, view.now) && (
              <button className="btn-primary" disabled={busy} onClick={() => familyAck(detail)} data-testid="cal-family-ack">
                {(() => { const l = ackLabel(detail); return t(l.key, l.params); })()}
              </button>
            )}
            <button className="btn-primary" onClick={() => openEdit(detail)} data-testid="cal-edit">{t('panelCal.edit')}</button>
            <button className="btn-danger" onClick={() => askDelete(detail)} data-testid="cal-delete">{t('panelCal.delete')}</button>
            <button className="btn-ghost" onClick={() => setDetail(null)}>{t('common.close')}</button>
          </div>
          {formErr && !scopeAsk && !confirmDel && <p className="error-text" role="alert">{t(formErr)}</p>}
        </Modal>
      )}

      {editing && !scopeAsk && (
        <Modal label={t(editing.kind === 'new' ? 'panelCal.form.titleAdd' : 'panelCal.form.titleEdit')} onClose={() => setEditing(null)}>
          <h2>{t(editing.kind === 'new' ? 'panelCal.form.titleAdd' : 'panelCal.form.titleEdit')}</h2>
          {editing.kind === 'edit' && editing.fromNotice && (
            <p className="text-secondary" data-testid="cal-edit-orig">
              {t('panelCal.form.orig', { when: origWhen(editing.before), title: editing.before.title })}
            </p>
          )}
          <CalEventForm draft={editing.draft} isNew={editing.kind === 'new'} recurringEdit={editing.kind === 'edit' && isRecurringOcc(editing.occ)}
            residents={residents} tz={tz}
            onChange={(draft) => { setFormErr(null); setEditing({ ...editing, draft } as Editing); }} />
          {formErr && <p className="error-text" role="alert" data-testid="cal-form-err">{t(formErr)}</p>}
          <div className="dv-pairing-actions">
            <button className="btn-primary" disabled={busy} onClick={save} data-testid="cal-save">
              {busy ? t('common.saving') : t(editing.kind === 'new' ? 'panelCal.form.add' : 'panelCal.form.save')}
            </button>
            <button className="btn-ghost" disabled={busy} onClick={() => setEditing(null)}>{t('common.cancel')}</button>
          </div>
        </Modal>
      )}

      {scopeAsk && (
        <Modal label={t(scopeAsk.mode === 'edit' ? 'panelCal.scope.editTitle' : 'panelCal.scope.deleteTitle')} onClose={() => setScopeAsk(null)}>
          <h2>{t(scopeAsk.mode === 'edit' ? 'panelCal.scope.editTitle' : 'panelCal.scope.deleteTitle')}</h2>
          <div role="radiogroup" className="pc-scopes" data-testid="cal-scope">
            {scopeAsk.scopes.map((s) => (
              <label key={s} className="pc-radio">
                <input type="radio" name="cal-scope" checked={scopeAsk.scope === s} data-testid={`cal-scope-${s}`}
                  onChange={() => setScopeAsk({ ...scopeAsk, scope: s, noteKey: scopeAsk.mode === 'edit' ? scopeNoteKey(scopeAsk.occ, s, now, scopeAsk.newStart) : null })} />
                {t(scopeAsk.mode === 'edit' ? `panelCal.scope.${s}` : `panelCal.scope.del${s[0].toUpperCase()}${s.slice(1)}`)}
              </label>
            ))}
          </div>
          {scopeAsk.noteKey && <p className="text-secondary" data-testid="cal-scope-note">{t(scopeAsk.noteKey)}</p>}
          {scopeAsk.mode === 'delete' && scopeAsk.scope === 'following' && <p className="text-secondary">{t('panelCal.scope.delFollowingNote')}</p>}
          {scopeAsk.clearWarn && scopeAsk.scope !== 'this' && <p className="pc-warn" data-testid="cal-clear-warn">{t('panelCal.scope.clearOverrides')}</p>}
          {formErr && <p className="error-text" role="alert">{t(formErr)}</p>}
          <div className="dv-pairing-actions">
            <button className={scopeAsk.mode === 'delete' ? 'btn-danger' : 'btn-primary'} disabled={busy} onClick={() => void scopeAsk.run(scopeAsk.scope)} data-testid="cal-scope-confirm">
              {t(scopeAsk.mode === 'edit' ? 'panelCal.scope.confirmEdit' : 'panelCal.scope.confirmDelete')}
            </button>
            <button className="btn-ghost" disabled={busy} onClick={() => setScopeAsk(null)}>{t('common.cancel')}</button>
          </div>
        </Modal>
      )}

      {confirmDel && (
        <Modal label={t('panelCal.deleteConfirm', { title: confirmDel.title })} onClose={() => setConfirmDel(null)}>
          <p>{t('panelCal.deleteConfirm', { title: confirmDel.title })}</p>
          {formErr && <p className="error-text" role="alert">{t(formErr)}</p>}
          <div className="dv-pairing-actions">
            <button className="btn-danger" disabled={busy} data-testid="cal-delete-confirm"
              onClick={() => void run(() => deleteCalEvent(id, confirmDel.event_id, ifMatchFor(confirmDel)), t('panelCal.deleted'))}>
              {t('panelCal.delete')}
            </button>
            <button className="btn-ghost" disabled={busy} onClick={() => setConfirmDel(null)}>{t('common.cancel')}</button>
          </div>
        </Modal>
      )}
    </section>
  );
}

function Modal({ label, onClose, children }: { label: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="pc-scrim" role="dialog" aria-modal="true" aria-label={label} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}>
      <div className="card pc-sheet">{children}</div>
    </div>
  );
}

function AckLine({ occ }: { occ: CalOccurrence }) {
  const { t } = useTranslation();
  if (!occ.ack) return null;
  return <>{ackRecordParts(occ.ack, occ.category).map((p) => t(p.key, p.params)).join(' · ')}</>;
}

function CalDetail({ occ, ruleSource, history, viewerText, dayLabel }: {
  occ: CalOccurrence; ruleSource: CalOccurrence; history: CalOccurrence[]; viewerText: (w: string) => string; dayLabel: (d: string) => string;
}) {
  const { t } = useTranslation();
  const spec = parseRRule(ruleSource.rrule, occ.start);
  const rep = spec ? repeatLabel(spec) : null;
  const repText = !ruleSource.rrule ? '' : rep
    ? t(rep.key, { ...rep.params, days: ((rep.params.days as string[] | undefined) ?? []).map((c) => t(`panelCal.wd.${c}`)).join(t('panelCal.rep.sep')) })
      + (spec?.end === 'until' ? t('panelCal.rep.until', { date: spec.until }) : spec?.end === 'count' ? t('panelCal.rep.count', { n: spec.count }) : '')
    : ruleSource.rrule;
  const rems = occ.reminders ?? [];
  return (
    <div data-testid="cal-detail">
      <h2>{CAT_ICON[occ.category] ?? '•'} {occ.title}</h2>
      <dl className="dv-info">
        <dt>{t('panelCal.detail.when')}</dt>
        <dd data-testid="cal-detail-when">
          {dayLabel(wallDate(occ.start))} {occ.all_day ? t('panelCal.allDay') : `${wallTime(occ.start)}–${wallTime(occ.end)}`}
          {!occ.all_day && <span className="text-secondary"> {viewerText(occ.start)}</span>}
        </dd>
        {repText && (<><dt>{t('panelCal.detail.repeat')}</dt><dd data-testid="cal-detail-repeat">{repText}</dd></>)}
        <dt>{t('panelCal.detail.reminders')}</dt>
        <dd data-testid="cal-detail-reminders">
          {rems.length === 0 ? t('panelCal.detail.noReminders') : rems.map((r) => { const l = reminderLabel(r, occ.all_day); return t(l.key, l.params); }).join(t('panelCal.rep.sep'))}
        </dd>
        {occ.for_whom && (<><dt>{t('panelCal.detail.forWhom')}</dt><dd>{occ.for_whom}</dd></>)}
        {occ.location && (<><dt>{t('panelCal.detail.location')}</dt><dd>{occ.location}</dd></>)}
        {occ.note && (<><dt>{t('panelCal.detail.note')}</dt><dd className="pc-note">{occ.note}</dd></>)}
        <dt>{t('panelCal.detail.category')}</dt>
        <dd>{t(`panelCal.cat.${CAL_CATEGORIES.includes(occ.category as never) ? occ.category : 'other'}`)}{occ.needs_ack ? ` · ${t('panelCal.detail.needsAck')}` : ''}</dd>
        {(occ.ack || occ.needs_ack) && (
          <>
            <dt>{t('panelCal.detail.ack')}</dt>
            <dd data-testid="cal-detail-ack">{occ.ack ? <AckLine occ={occ} /> : t('panelCal.detail.ackNone')}</dd>
          </>
        )}
        {history.filter((h) => h !== occ).length > 0 && (
          <>
            <dt>{t('panelCal.detail.ackHistory')}</dt>
            <dd>
              <ul className="pc-ack-history" data-testid="cal-ack-history">
                {history.filter((h) => h !== occ).map((h) => (
                  <li key={`${h.event_id}-${h.recurrence_id}`}>
                    {dayLabel(wallDate(h.start))}{!h.all_day && ` ${wallTime(h.start)}`}：<AckLine occ={h} />
                  </li>
                ))}
              </ul>
            </dd>
          </>
        )}
      </dl>
      <p className="text-secondary" data-testid="cal-detail-by">
        {occ.source === 'voice' ? t('panelCal.detail.createdByVoice') : t('panelCal.detail.createdBy', { name: occ.created_by_name })}
        {occ.updated_by_name && occ.updated_by_name !== occ.created_by_name && ` · ${t('panelCal.detail.updatedBy', { name: occ.updated_by_name })}`}
      </p>
    </div>
  );
}

const REPEAT_KINDS: RepeatKind[] = ['none', 'daily', 'weekly', 'monthly', 'yearly'];

export function CalEventForm({ draft, isNew, recurringEdit, residents, tz, onChange }: {
  draft: CalDraft; isNew: boolean; recurringEdit: boolean; residents: string[]; tz: string; onChange: (d: CalDraft) => void;
}) {
  const { t } = useTranslation();
  const set = (p: Partial<CalDraft>) => onChange({ ...draft, ...p });
  const setRep = (p: Partial<RepeatSpec>) => onChange({ ...draft, rawRRule: null, repeat: { ...draft.repeat, ...p } });
  const opts = draft.allDay ? ALL_DAY_REMINDER_OPTIONS : TIMED_REMINDER_OPTIONS;
  const chosen = new Set(draft.reminders.map(reminderId));
  const toggleRem = (r: CalReminder, on: boolean) => {
    const next = on ? [...draft.reminders, r] : draft.reminders.filter((x) => reminderId(x) !== reminderId(r));
    set({ reminders: opts.filter((o) => next.some((x) => reminderId(x) === reminderId(o))) });
  };
  const [ackTouched, setAckTouched] = useState(false);
  const rep = draft.repeat;
  return (
    <div className="pc-form" data-testid="cal-form">
      <label className="pc-field">
        <span>{t('panelCal.form.name')}</span>
        <input value={draft.title} maxLength={40} data-testid="cal-f-title"
          onChange={(e) => {
            const title = e.target.value;
            // 新增時標題含「藥」預設要長輩確認（家人自己動過開關就不再自動改）
            onChange({ ...draft, title, needsAck: isNew && !ackTouched ? title.includes('藥') : draft.needsAck });
          }} />
      </label>
      <div className="pc-row">
        <label className="pc-field">
          <span>{t('panelCal.form.date')}</span>
          <input type="date" value={draft.date} data-testid="cal-f-date" onChange={(e) => set({ date: e.target.value })} />
        </label>
        {!draft.allDay && (
          <label className="pc-field">
            <span>{t('panelCal.form.time')}</span>
            <input type="time" value={draft.time} data-testid="cal-f-time" onChange={(e) => set({ time: e.target.value })} />
          </label>
        )}
        {!draft.allDay && (
          <label className="pc-field">
            <span>{t('panelCal.form.duration')}</span>
            <input type="number" min={5} max={720} step={5} value={draft.durationMin} data-testid="cal-f-duration" onChange={(e) => set({ durationMin: Number(e.target.value) })} />
          </label>
        )}
        <label className="pc-check">
          <input type="checkbox" checked={draft.allDay} data-testid="cal-f-allday"
            onChange={(e) => set({ allDay: e.target.checked, reminders: draft.reminders.filter((r) => (e.target.checked ? ALL_DAY_REMINDER_OPTIONS : TIMED_REMINDER_OPTIONS).some((o) => reminderId(o) === reminderId(r))) })} />
          {t('panelCal.form.allDay')}
        </label>
      </div>

      <fieldset className="pc-fieldset">
        <legend>{t('panelCal.form.repeat')}</legend>
        {draft.rawRRule !== null ? (
          <p className="text-secondary">
            {t('panelCal.form.rawRule', { rule: draft.rawRRule })}{' '}
            <button className="btn-ghost" onClick={() => setRep({ kind: 'daily' })}>{t('panelCal.form.replaceRule')}</button>
          </p>
        ) : (
          <>
            <select value={rep.kind} data-testid="cal-f-repeat" aria-label={t('panelCal.form.repeat')} onChange={(e) => setRep({ kind: e.target.value as RepeatKind })}>
              {REPEAT_KINDS.filter((k) => !(recurringEdit && k === 'none' && rep.kind !== 'none')).map((k) => <option key={k} value={k}>{t(`panelCal.rep.kind.${k}`)}</option>)}
            </select>
            {(rep.kind === 'daily' || rep.kind === 'weekly') && (
              <label className="pc-inline">
                {t('panelCal.form.interval')}
                <input type="number" min={1} max={99} value={rep.interval} data-testid="cal-f-interval" onChange={(e) => setRep({ interval: Number(e.target.value) || 1 })} />
                {t(rep.kind === 'daily' ? 'panelCal.view.day' : 'panelCal.view.week')}
              </label>
            )}
            {rep.kind === 'weekly' && (
              <div className="pc-wdpick" role="group" aria-label={t('panelCal.form.weekdays')}>
                {WEEKDAY_CODES.map((c) => (
                  <label key={c} className="pc-check">
                    <input type="checkbox" checked={rep.byday.includes(c)} data-testid={`cal-f-wd-${c}`}
                      onChange={(e) => setRep({ byday: e.target.checked ? [...rep.byday, c] : rep.byday.filter((x) => x !== c) })} />
                    {t(`panelCal.wd.${c}`)}
                  </label>
                ))}
              </div>
            )}
            {rep.kind === 'monthly' && (
              <label className="pc-inline">
                {t('panelCal.form.monthday')}
                <select value={rep.monthday} data-testid="cal-f-monthday" onChange={(e) => setRep({ monthday: Number(e.target.value) })}>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
                  <option value={-1}>{t('panelCal.form.monthLast')}</option>
                </select>
                {rep.monthday >= 29 && <small className="text-secondary">{t('panelCal.form.monthdayNote')}</small>}
              </label>
            )}
            {rep.kind !== 'none' && (
              <div className="pc-row">
                <select value={rep.end} aria-label={t('panelCal.form.end')} data-testid="cal-f-end" onChange={(e) => setRep({ end: e.target.value as RepeatSpec['end'] })}>
                  <option value="never">{t('panelCal.form.endNever')}</option>
                  <option value="until">{t('panelCal.form.endUntil')}</option>
                  <option value="count">{t('panelCal.form.endCount')}</option>
                </select>
                {rep.end === 'until' && <input type="date" value={rep.until} aria-label={t('panelCal.form.endUntil')} data-testid="cal-f-until" onChange={(e) => setRep({ until: e.target.value })} />}
                {rep.end === 'count' && <input type="number" min={1} max={500} value={rep.count} aria-label={t('panelCal.form.endCount')} data-testid="cal-f-count" onChange={(e) => setRep({ count: Number(e.target.value) || 1 })} />}
              </div>
            )}
          </>
        )}
      </fieldset>

      <fieldset className="pc-fieldset">
        <legend>{t('panelCal.form.reminders')}</legend>
        {draft.allDay && <p className="text-secondary pc-hint" data-testid="cal-allday-anchor">{t('panelCal.form.allDayAnchorHint')}</p>}
        <div className="pc-rems">
          {opts.map((o) => {
            const l = reminderLabel(o, draft.allDay);
            const on = chosen.has(reminderId(o));
            const lockedByAck = draft.needsAck && o.offset_min === 0;
            return (
              <label key={reminderId(o)} className="pc-check">
                <input type="checkbox" checked={on || lockedByAck} disabled={lockedByAck || (!on && draft.reminders.length >= CAL_MAX_REMINDERS)}
                  data-testid={`cal-f-rem-${reminderId(o)}`} onChange={(e) => toggleRem(o, e.target.checked)} />
                {t(l.key, l.params)}
              </label>
            );
          })}
        </div>
      </fieldset>

      <label className="pc-check">
        <input type="checkbox" checked={draft.needsAck} data-testid="cal-f-ack" onChange={(e) => { setAckTouched(true); set({ needsAck: e.target.checked }); }} />
        {t('panelCal.form.needsAck')}
      </label>
      {draft.needsAck && <p className="text-secondary pc-hint">{t('panelCal.form.needsAckHint')}</p>}

      <div className="pc-row">
        {/* 給誰：residents（主人在面板頁設）；沒設長輩名字就不顯示 */}
        {forWhomOptions(residents, draft.forWhom).length > 0 && (
          <label className="pc-field">
            <span>{t('panelCal.form.forWhom')}</span>
            <select value={draft.forWhom} data-testid="cal-f-forwhom" onChange={(e) => set({ forWhom: e.target.value })}>
              <option value="">{t('panelCal.form.forWhomNone')}</option>
              {forWhomOptions(residents, draft.forWhom).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        )}
        <label className="pc-field">
          <span>{t('panelCal.form.category')}</span>
          <select value={draft.category} data-testid="cal-f-category" onChange={(e) => set({ category: e.target.value })}>
            {isNew && <option value="">{t('panelCal.cat.auto')}</option>}
            {CAL_CATEGORIES.map((c) => <option key={c} value={c}>{CAT_ICON[c]} {t(`panelCal.cat.${c}`)}</option>)}
          </select>
        </label>
      </div>

      <label className="pc-field">
        <span>{t('panelCal.form.location')}</span>
        <input value={draft.location} maxLength={40} data-testid="cal-f-location" onChange={(e) => set({ location: e.target.value })} />
      </label>
      <label className="pc-field">
        <span>{t('panelCal.form.note')}</span>
        <textarea value={draft.note} maxLength={200} rows={2} data-testid="cal-f-note" onChange={(e) => set({ note: e.target.value })} />
      </label>
      {tz && <p className="text-secondary pc-hint" data-testid="cal-form-tz">{t('panelCal.tzNote', { tz })}</p>}
    </div>
  );
}

/** ask_family 預填時旁邊顯示的「原本：10/29 10:00」（整天只有日期） */
function origWhen(d: CalDraft): string {
  const md = d.date ? `${Number(d.date.slice(5, 7))}/${Number(d.date.slice(8, 10))}` : '';
  return d.allDay || !d.time ? md : `${md} ${d.time}`;
}
